"use client";

import dynamic from "next/dynamic";
import { useMemo, useState } from "react";
import { playSound } from "@/lib/game/audio";
import { CATEGORY_LABELS, CATEGORY_ORDER } from "@/lib/game/partCategories";
import { PART_BY_ID, SHOP_PARTS } from "@/lib/game/parts";
import { canAffordPart, isPartUnlocked } from "@/lib/game/progress";
import type { PartCategory } from "@/lib/game/types";
import { categoryIconId } from "@/lib/game/uiIcons";
import { GameIconButton, GamePanel, GameScreenHeader } from "../game-ui";
import { MenuScreenShell } from "../MenuScreenShell";
import { useGame } from "../GameProvider";

const PixiPartThumbnail = dynamic(
  () => import("../PixiPartThumbnail").then((m) => m.PixiPartThumbnail),
  { ssr: false },
);

const CATEGORY_TONES = ["sky", "violet", "rose", "amber", "emerald", "indigo"] as const;

export function StarShopScreen() {
  const { goTo, progress, buyPart } = useGame();
  const [activeCategory, setActiveCategory] = useState<PartCategory>("nose");

  const grouped = useMemo(() => {
    const map = new Map<PartCategory, typeof SHOP_PARTS>();
    for (const category of CATEGORY_ORDER) {
      map.set(
        category,
        SHOP_PARTS.filter((p) => p.category === category).sort(
          (a, b) => (a.starCost ?? 0) - (b.starCost ?? 0),
        ),
      );
    }
    return map;
  }, []);

  const ownedShopCount = SHOP_PARTS.filter((p) => isPartUnlocked(progress, p.id)).length;
  const activeParts = grouped.get(activeCategory) ?? [];
  const visibleCategories = CATEGORY_ORDER.filter((c) => (grouped.get(c)?.length ?? 0) > 0);

  return (
    <MenuScreenShell variant="menu">
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-5 py-6">
        <GameScreenHeader
          title="Star Shop"
          subtitle="Spend stars from Play to unlock rocket parts for your hangar."
          onBack={() => goTo("home")}
        />

        <div className="mx-auto w-full max-w-md flex-1 pb-8">
          <GamePanel tone="gold" className="mb-6 text-center">
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-100/80">
              Your balance
            </p>
            <p className="mt-1 text-4xl font-extrabold text-amber-200">
              {progress.starBalance}{" "}
              <span className="text-3xl" aria-hidden>
                ⭐
              </span>
            </p>
            <p className="mt-2 text-xs font-medium text-amber-100/80">
              {ownedShopCount} of {SHOP_PARTS.length} shop parts owned
            </p>
            <div className="mt-4 flex justify-center">
              <GameIconButton
                icon="earn"
                tone="amber"
                size="lg"
                label="Go earn stars"
                onClick={() => goTo("activities")}
              />
            </div>
          </GamePanel>

          <div
            className="mb-4 flex gap-2 overflow-x-auto pb-1"
            role="tablist"
            aria-label="Part categories"
          >
            {visibleCategories.map((category, index) => {
              const selected = category === activeCategory;
              return (
                <GameIconButton
                  key={category}
                  icon={categoryIconId(category)}
                  tone={CATEGORY_TONES[index % CATEGORY_TONES.length]}
                  size="sm"
                  selected={selected}
                  label={CATEGORY_LABELS[category]}
                  role="tab"
                  aria-selected={selected}
                  onClick={() => setActiveCategory(category)}
                />
              );
            })}
          </div>

          <section role="tabpanel" aria-label={CATEGORY_LABELS[activeCategory]}>
            <h2 className="mb-3 text-sm font-bold uppercase tracking-wide text-violet-100/80">
              {CATEGORY_LABELS[activeCategory]}
            </h2>
            {activeParts.length > 0 ? (
              <ul className="grid grid-cols-2 gap-3">
                {activeParts.map((part) => {
                  const owned = isPartUnlocked(progress, part.id);
                  const affordable = canAffordPart(progress, part.id);
                  const def = PART_BY_ID[part.id];

                  return (
                    <li key={part.id}>
                      <GamePanel
                        tone="cosmic"
                        className={`flex flex-col !p-3 ${owned ? "border-emerald-300/35" : affordable ? "border-sky-300/25" : ""}`}
                      >
                        <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-xl border-2 border-white/10 bg-black/20">
                          <PixiPartThumbnail partId={part.id} className="h-[4.5rem] w-[4.5rem]" />
                        </div>
                        <p className="mt-2 text-center text-sm font-bold leading-tight text-white">
                          {def?.name ?? part.name}
                        </p>
                        <div className="mt-2 flex justify-center">
                          {owned ? (
                            <GameIconButton icon="owned" tone="emerald" size="sm" disabled />
                          ) : (
                            <GameIconButton
                              icon="buy"
                              tone={affordable ? "amber" : "slate"}
                              size="sm"
                              badge={part.starCost}
                              label={`Buy for ${part.starCost} stars`}
                              disabled={!affordable}
                              onClick={() => {
                                if (!buyPart(part.id)) return;
                                playSound("unlock");
                              }}
                            />
                          )}
                        </div>
                      </GamePanel>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="rounded-2xl border border-dashed border-violet-300/25 bg-indigo-950/50 px-4 py-8 text-center text-sm text-violet-100/70">
                No parts in this category yet.
              </p>
            )}
          </section>
        </div>
      </div>
    </MenuScreenShell>
  );
}
